/**
 * store/axios.js — Authenticated Axios instance
 *
 * Migrated from aws-amplify v5 + aws4 to @nestingale/auth v6 signing.
 * Uses createSigningInterceptor from @nestingale/auth/signing for SigV4.
 */

import axios from 'axios';
import { getAmplifyBackend } from '@nestingale/auth';
import { createSigningInterceptor, signRequest } from '@nestingale/auth/signing';
import {
  getGoogleCredentialsFromLS,
  getFBKCredentialsFromLS,
  getAppleCredentialsFromLS,
  signInWithAppleRedirect,
} from '@nestingale/auth';
import { signOutUser } from '@/utils/useUserInfo';
import { configureAmplify } from '@/app/providers/AuthProvider';

if (typeof window !== 'undefined') {
  window.nestingale = window.nestingale || {};
  window.nestingale.credentials = '';
  window.nestingale.promptLoginId = null;
  window.nestingale.retryAPIURLsArray = [];

  /**
   * Get AWS credentials for API signing.
   * Uses @nestingale/auth v6 getAmplifyBackend().getCredentials() instead of v5 Auth.currentAuthenticatedUser/currentCredentials.
   */
  window.nestingale.getCredentialsForIdentity = function () {
    return new Promise(async (resolve, reject) => {
      try {
        // Use @nestingale/auth v6 backend to get credentials
        const backend = getAmplifyBackend();
        const credentials = await backend.getCredentials();

        if (credentials && credentials.authenticated) {
          return resolve(credentials);
        } else {
          // Not authenticated — fall through to social/guest handling below.
          throw new Error('No authenticated credentials');
        }
      } catch (error) {
        const googleCredentials = getGoogleCredentialsFromLS();
        const fBkCredentials = getFBKCredentialsFromLS();
        const appleCredentials = getAppleCredentialsFromLS();

        if (googleCredentials) {
          // Google user — prompt re-auth by signing out and redirecting to login
          function promptLogin() {
            if (window.google && window.google?.accounts) {
              clearInterval(window.nestingale.promptLoginId);
              // Prompt Google One Tap
              try {
                window.google.accounts.id.prompt((notification) => {
                  if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    signOutUser(true);
                  }
                });
              } catch (e) {
                signOutUser(true);
              }
            } else {
              console.log('reset the GOOGLE ls and redirect to login');
              signOutUser(true);
            }
          }
          if (!window.nestingale.promptLoginId) {
            window.nestingale.promptLoginId = setInterval(promptLogin, 50);
          }
        } else if (fBkCredentials) {
          function promptLogin() {
            if (window.FB) {
              clearInterval(window.nestingale.promptLoginId);
              // Re-authenticate with Facebook
              window.FB.login(
                (response) => {
                  if (!response || !response.authResponse) return;
                  // Facebook re-auth handled
                },
                { scope: 'public_profile,email' }
              );
            } else {
              console.log('reset the FB ls and redirect to login');
              signOutUser(true);
            }
          }
          if (!window.nestingale.promptLoginId) {
            window.nestingale.promptLoginId = setInterval(promptLogin, 50);
          }
        } else if (appleCredentials) {
          function promptLogin() {
            console.log('reset the APPLE ls and redirect to login');
            clearInterval(window.nestingale.promptLoginId);
            signInWithAppleRedirect();
          }
          if (!window.nestingale.promptLoginId) {
            window.nestingale.promptLoginId = setInterval(promptLogin, 50);
          }
        } else {
          // Guest / no-Identity-Pool (e.g. GTR): try unauthenticated credentials.
          // If none available, resolve with null so requests proceed unsigned
          // rather than rejecting and flooding the console.
          try {
            const backend = getAmplifyBackend();
            const guestCredentials = await backend.getCredentials();
            if (guestCredentials) {
              return resolve(guestCredentials);
            }
          } catch (e) {
            // ignore — fall through to null resolve
          }
          resolve(null);
        }
      }
    });
  };
}

const axiosObj = axios.create({
  baseURL: 'https://d221a61rb87vel.cloudfront.net/v1',
});

export function getSignedRequestObject(method, endpoint, postData) {
  if (typeof window === 'undefined' || !window.nestingale.credentials) {
    return { url: endpoint, method, data: postData, headers: { 'content-type': 'application/json' } };
  }

  const creds = window.nestingale.credentials;
  const signedHeaders = signRequest(
    {
      method,
      url: endpoint,
      data: postData,
      headers: { 'content-type': 'application/json' },
    },
    {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
    { service: 'execute-api', region: 'us-east-1' }
  );

  // Remove headers that shouldn't be sent to browser
  if (signedHeaders) {
    delete signedHeaders['Host'];
    delete signedHeaders['Content-Length'];
  }

  return {
    url: endpoint,
    method,
    data: postData,
    headers: signedHeaders,
  };
}

function shouldSkipInterceptor(endpoint) {
  return endpoint?.includes('/customer/savecustomerprofile');
}

// Request interceptor — uses @nestingale/auth signing
axiosObj.interceptors.request.use(
  async function (config) {
    try {
      if (shouldSkipInterceptor(config.url)) {
        return config;
      }

      if (typeof window !== 'undefined') {
        // '' = not yet fetched; null = fetched but no creds available (e.g. no Identity Pool)
        if (window.nestingale.credentials === '') {
          const credentials = await window.nestingale.getCredentialsForIdentity();
          // Use a sentinel so we don't re-call on every request when no creds exist
          window.nestingale.credentials = credentials ?? null;
          window.nestingale.retryAPIURLsArray = [];
        }

        // Merge headers
        config.headers = Object.assign(
          { 'content-type': 'application/json' },
          config.headers
        );

        if (!config.url.startsWith('https')) {
          config.url = config.baseURL + config.url;
        }

        // Use @nestingale/auth signRequest for SigV4 signing
        const creds = window.nestingale.credentials;
        if (creds?.accessKeyId) {
          const signedHeaders = signRequest(
            {
              method: config.method?.toUpperCase() || 'GET',
              url: config.url,
              data: config.data,
            },
            {
              accessKeyId: creds.accessKeyId,
              secretAccessKey: creds.secretAccessKey,
              sessionToken: creds.sessionToken,
            },
            { service: 'execute-api', region: 'us-east-1' }
          );
          config.headers = { ...config.headers, ...signedHeaders };
        }
      }
    } catch (e) {
      console.log(e);
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Response interceptor — retry on 401
axiosObj.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    if (typeof window !== 'undefined' && error.request && error.request.status === 401) {
      if (window.nestingale.retryAPIURLsArray.includes(error.config.url)) {
        await window.nestingale.getCredentialsForIdentity();
        return;
      }
      window.nestingale.retryAPIURLsArray.push(error.config.url);
      window.nestingale.credentials = '';
      return this.request(error.config);
    }
    throw error;
  }.bind(axiosObj)
);

export default axiosObj;
